<template>
  <!-- AI 助手悬浮按钮 -->
  <div class="ai-assistant-container">
    <!-- 悬浮按钮 -->
    <transition name="fade">
      <button
        v-if="!isExpanded"
        @click="toggleExpanded"
        class="fixed bottom-10 right-10 z-40 flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-purple-700 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-300 transition-all duration-300 group"
        title="使用AI智能生成邮件模板"
      >
        <svg class="w-4 h-4 group-hover:animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <span class="text-sm font-medium whitespace-nowrap">AI生成</span>
        <!-- 脉动光环效果 -->
        <span class="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 animate-slow-ping"></span>
      </button>
    </transition>

    <!-- AI 助手面板 -->
    <transition name="slide-up">
      <div v-if="isExpanded" class="ai-panel">
        <!-- 头部 -->
        <div class="ai-panel-header">
          <div class="flex items-center">
            <h3 class="text-base font-semibold text-gray-800">AI 写邮件助手</h3>
          </div>
          <button
            @click="toggleExpanded"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- 内容区域 -->
        <div class="ai-panel-content" @click="closeAllDropdowns">
          <!-- 模板类型选择 -->
          <div class="form-group">
            <label class="form-label">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              模板类型
            </label>
            <div class="relative" ref="typeSelectRef" @click.stop>
              <!-- 触发器 -->
              <div
                @click.stop="toggleTypeDropdown"
                class="relative w-full h-10 cursor-pointer bg-white border border-gray-300 rounded-lg px-3 text-left shadow-sm hover:border-blue-500 hover:ring-1 hover:ring-blue-500 transition-colors flex items-center"
                :class="{ 'border-blue-500 ring-1 ring-blue-500': isTypeOpen }"
              >
                <span class="flex-1 truncate text-sm text-gray-900">
                  {{ templateTypes.find(t => t.key === selectedType)?.name || '请选择模板类型' }}
                </span>
                <span class="pointer-events-none flex items-center pl-2">
                  <svg 
                    class="h-5 w-5 text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': isTypeOpen }"
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                  </svg>
                </span>
              </div>
              
              <!-- 下拉列表 -->
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div
                  v-show="isTypeOpen"
                  class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
                >
                  <div
                    v-for="type in templateTypes"
                    :key="type.key"
                    @click.stop="selectType(type.key)"
                    class="group relative cursor-pointer select-none py-3 pl-3 pr-9 text-gray-900 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                    :class="{ 'bg-blue-100 text-blue-900': selectedType === type.key }"
                  >
                    <span class="block truncate text-sm font-medium">
                {{ type.name }}
                    </span>
                    <span 
                      v-if="selectedType === type.key"
                      class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                    >
                      <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </span>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- 语言选择 -->
          <div class="form-group">
            <label class="form-label">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              模板语言
            </label>
            <div class="relative" ref="languageSelectRef" @click.stop>
              <!-- 触发器 -->
              <div
                @click.stop="toggleLanguageDropdown"
                class="relative w-full h-10 cursor-pointer bg-white border border-gray-300 rounded-lg px-3 text-left shadow-sm hover:border-blue-500 hover:ring-1 hover:ring-blue-500 transition-colors flex items-center"
                :class="{ 'border-blue-500 ring-1 ring-blue-500': isLanguageOpen }"
              >
                <span class="flex-1 truncate text-sm text-gray-900">
                  {{ languages.find(l => l.code === selectedLanguage)?.name || '请选择语言' }}
                </span>
                <span class="pointer-events-none flex items-center pl-2">
                  <svg 
                    class="h-5 w-5 text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': isLanguageOpen }"
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                  </svg>
                </span>
              </div>
              
              <!-- 下拉列表 -->
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div
                  v-show="isLanguageOpen"
                  class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
                >
                  <div
                    v-for="lang in languages"
                    :key="lang.code"
                    @click.stop="selectLanguage(lang.code)"
                    class="group relative cursor-pointer select-none py-3 pl-3 pr-9 text-gray-900 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                    :class="{ 'bg-blue-100 text-blue-900': selectedLanguage === lang.code }"
                  >
                    <span class="block truncate text-sm font-medium">
                {{ lang.name }}
                    </span>
                    <span 
                      v-if="selectedLanguage === lang.code"
                      class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                    >
                      <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </span>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- 邮件内容 -->
          <div class="form-group">
            <label class="form-label">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              邮件内容
              <span class="text-red-500 ml-1">*</span>
            </label>
            <textarea
              v-model="subject"
              placeholder="告诉AI要生成什么邮件，例如：欢迎新用户注册、感谢客户购买、春季促销活动、订单发货通知..."
              class="form-textarea"
              rows="2"
              :disabled="isGenerating"
            ></textarea>
          </div>

          <!-- 自定义提示词（仅当选择自定义类型时显示） -->
          <div v-if="selectedType === 'custom'" class="form-group">
            <label class="form-label">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              自定义提示词
              <span class="text-red-500 ml-1">*</span>
            </label>
            <textarea
              v-model="customPrompt"
              placeholder="描述你想要的邮件内容，例如：生成一封感谢客户购买的邮件，语气要温暖友好，包含产品推荐..."
              class="form-textarea"
              rows="3"
              :disabled="isGenerating"
            ></textarea>
          </div>

          <!-- 生成提示 -->
          <div v-if="!isGenerating && !errorMessage" class="text-xs text-gray-500 mb-4">
            <svg class="w-3.5 h-3.5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            AI 将根据您的需求生成专业的邮件模板内容
          </div>

          <!-- 错误提示 -->
          <div v-if="errorMessage" class="error-message">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {{ errorMessage }}
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="ai-panel-footer">
          <button
            @click="toggleExpanded"
            class="btn-secondary"
            :disabled="isGenerating"
          >
            取消
          </button>
          <button
            @click="generateTemplate"
            class="btn-primary"
            :disabled="!canGenerate || isGenerating"
          >
            <div v-if="isGenerating" class="flex items-center">
              <div class="animate-spin h-4 w-4 mr-2">
                <svg class="w-4 h-4" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <span>生成中 {{ progress }}%</span>
            </div>
            <div v-else class="flex items-center">
            <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
              <span>生成邮件</span>
            </div>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { apiRequest } from '@/utils/api'

// Props
const props = defineProps({
  onGenerated: {
    type: Function,
    required: true
  }
})

// 状态
const isExpanded = ref(false)
const isGenerating = ref(false)
const errorMessage = ref('')
const progress = ref(0)
let progressTimer = null

// 下拉框状态和引用
const isTypeOpen = ref(false)
const isLanguageOpen = ref(false)
const typeSelectRef = ref(null)
const languageSelectRef = ref(null)

// 表单数据
const selectedType = ref('welcome')
const selectedLanguage = ref('en')
const subject = ref('')
const customPrompt = ref('')

// 预设数据
const templateTypes = ref([])
const languages = ref([])

// 计算属性
const canGenerate = computed(() => {
  if (!subject.value.trim()) {
    return false
  }
  if (selectedType.value === 'custom' && !customPrompt.value.trim()) {
    return false
  }
  return true
})

// 下拉框方法
const toggleTypeDropdown = () => {
  isTypeOpen.value = !isTypeOpen.value
  if (isLanguageOpen.value) isLanguageOpen.value = false
}

const toggleLanguageDropdown = () => {
  isLanguageOpen.value = !isLanguageOpen.value
  if (isTypeOpen.value) isTypeOpen.value = false
}

const selectType = (value) => {
  selectedType.value = value
  isTypeOpen.value = false
}

const selectLanguage = (value) => {
  selectedLanguage.value = value
  isLanguageOpen.value = false
}

// 关闭所有下拉框
const closeAllDropdowns = () => {
  isTypeOpen.value = false
  isLanguageOpen.value = false
}

// 点击外部关闭下拉框
const handleClickOutside = (event) => {
  if (typeSelectRef.value && !typeSelectRef.value.contains(event.target)) {
    isTypeOpen.value = false
  }
  if (languageSelectRef.value && !languageSelectRef.value.contains(event.target)) {
    isLanguageOpen.value = false
  }
}

// 切换展开/收起
const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
  if (!isExpanded.value) {
    // 收起时清除错误信息和下拉框状态
    errorMessage.value = ''
    closeAllDropdowns()
  }
}

// 加载模板类型和语言列表
const loadOptions = async () => {
  try {
    // 加载模板类型
    const typesResponse = await apiRequest.get('/api/email-templates/v2/ai/template-types/')
    if (typesResponse.data.success) {
      templateTypes.value = typesResponse.data.template_types
    }

    // 加载语言列表
    const langsResponse = await apiRequest.get('/api/email-templates/v2/ai/languages/')
    if (langsResponse.data.success) {
      languages.value = langsResponse.data.languages
    }
  } catch (error) {
    console.error('加载选项失败:', error)
  }
}

// 生成邮件模板
const generateTemplate = async () => {
  if (!canGenerate.value) {
    return
  }

  isGenerating.value = true
  errorMessage.value = ''
  progress.value = 0

  // 启动进度条（1分钟 = 60秒，每秒更新一次）
  progressTimer = setInterval(() => {
    if (progress.value < 95) {
      progress.value += Math.floor(100 / 60)  // 每秒增加约1.67%
      if (progress.value > 95) progress.value = 95  // 最多到95%
    }
  }, 1000)

  try {
    const response = await apiRequest.post('/api/email-templates/v2/ai/generate/', {
      subject: subject.value.trim(),
      template_type: selectedType.value,
      language: selectedLanguage.value,
      custom_prompt: selectedType.value === 'custom' ? customPrompt.value.trim() : undefined
    })

    if (response.data.success) {
      // 完成时设置为100%
      progress.value = 100
      
      // 通知父组件生成成功
      props.onGenerated(response.data.html_content)
      
      // 重置表单
      subject.value = ''
      customPrompt.value = ''
      
      // 收起面板
      isExpanded.value = false
    } else {
      errorMessage.value = response.data.error || '生成失败'
    }
  } catch (error) {
    console.error('生成邮件模板失败:', error)
    errorMessage.value = error.response?.data?.error || '生成失败，请稍后重试'
  } finally {
    // 清除进度条定时器
    if (progressTimer) {
      clearInterval(progressTimer)
      progressTimer = null
    }
    isGenerating.value = false
    progress.value = 0
  }
}

// 生命周期钩子
onMounted(() => {
  loadOptions()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  // 清除进度条定时器
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
})
</script>

<style scoped>
.ai-panel {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 420px;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 100px);
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.ai-panel-header h3 {
  color: white;
}

.ai-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.ai-panel-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  transition: all 0.2s;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.form-input:disabled,
.form-textarea:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.form-textarea {
  resize: none;
  font-family: inherit;
}

.error-message {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 13px;
  margin-bottom: 12px;
}

.btn-primary,
.btn-secondary {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover:not(:disabled) {
  background: #f9fafb;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 动画效果 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

/* AI按钮慢速脉冲动画 */
@keyframes slow-ping {
  0% {
    transform: scale(1);
    opacity: 0.2;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.1;
  }
  100% {
    transform: scale(1.2);
    opacity: 0;
  }
}

.animate-slow-ping {
  animation: slow-ping 2.5s cubic-bezier(0, 0, 0.2, 1) infinite;
}

/* 响应式设计 */
@media (max-width: 640px) {
  .ai-panel {
    width: calc(100vw - 32px);
    bottom: 16px;
    right: 16px;
  }
}
</style>

